/**
 * @fileoverview useResource — async-data primitive (RES-PRIM-001)
 * @package @puffin-core/page-shell
 *
 * Closes the screen-204 UAT bug class by replacing the ambiguous
 * `useState([])` empty-array sentinel with an explicit
 * `{status, data, error, refetch}` discriminated-union shape.
 *
 * Callers cannot accidentally conflate "loading" with "empty" because
 * `data` is `undefined` until `status === 'ready'`.
 *
 * Built-in `AbortController` cancellation on unmount + deps change. The
 * fetcher receives `({signal})`; if it threads the signal into its
 * fetch/axios/realtime call, the actual HTTP request is cancelled.
 * Otherwise the hook still silently drops the stale resolution.
 *
 * Standard: GUIDES/shared/standards/async-data-loading.md (RES-PRIM-001)
 *
 * @typedef {('idle'|'loading'|'ready'|'error')} ResourceStatus
 *
 * @typedef {Object} ResourceState
 * @property {ResourceStatus} status
 * @property {*} [data]
 * @property {Error} [error]
 * @property {() => Promise<void>} refetch - a FULL reload: blanks `data` and
 *   returns to `status: 'loading'` (a manual Retry shows its loading state).
 * @property {() => Promise<void>} revalidate - a BACKGROUND re-read: from
 *   `ready` it keeps `status: 'ready'` and the current `data` while in flight,
 *   swaps the new data in when it lands, and on failure keeps the data and sets
 *   `revalidateError`. From any other status there is nothing to keep, so it is
 *   a full load.
 * @property {boolean} revalidating - true only while a `revalidate()` that
 *   began from `ready` is in flight.
 * @property {Error} [revalidateError] - the last background re-read's failure;
 *   cleared when the next `revalidate()` begins and by any successful read.
 *
 * ## `refetch` vs `revalidate` (PLN-DEPT-PLAN-001 R6, F39)
 *
 * `refetch` drops `data` on every call, so a consumer that re-reads behind a
 * live surface — a poll, a return-to-tab refresh — unmounted that surface (and
 * whatever the user had open or focused in it) on every tick. prep-web and
 * patrol-web each hand-rolled a "last ready payload" `useState` mirror to
 * survive it. `revalidate` is that mirror, owned once: the page stays up, the
 * new data replaces the old atomically, and a failed background read is
 * reported beside the data instead of replacing it. `refetch`'s semantics are
 * unchanged and pinned by `useResource.revalidate.test.js`'s first describe.
 *
 * ## `enabled` — the auto-load switch, read on EVERY deps cycle (L-MIRROR2, Q-R8MIRROR-1)
 *
 * While `enabled` is truthy the fetcher auto-runs on mount and on every deps
 * change; while it is falsy nothing auto-runs and the caller drives the load
 * with `refetch()` / `revalidate()` (the manual mode — a lookup form, an
 * audit query run on demand). It is part of every effect cycle, exactly like
 * `useAbortableFetch`'s `enabled`: turning it ON loads, turning it OFF
 * cancels the automatic load in flight (a resource that had not finished
 * loading returns to `idle`; a `ready` one keeps its data — it is still the
 * last answer).
 *
 * It replaced `{ initial: 'idle' | 'loading' }`, which was captured in a ref
 * at MOUNT: a consumer whose auto-load condition became true later — planner's
 * shift-template wizard, whose station list waits for the operator to pick a
 * location — never loaded at all. The render that turns `enabled` on already
 * reports `loading` (never a committed `idle` beside an enabled switch), and
 * a deps change while disabled settles an aborted manual load back to `idle`
 * instead of leaving it `loading` forever.
 *
 * The options record is CLOSED: an unknown key (a stale `initial`, a typo)
 * throws, because silently ignoring it would auto-load a resource its caller
 * meant to hold.
 *
 * @typedef {Object} UseResourceOptions
 * @property {*} [enabled=true] - truthy: auto-load; falsy: manual mode.
 * @property {number} [pollIntervalMs] - When a positive finite number, the
 *   fetcher re-runs on a `setInterval` of this many milliseconds after the
 *   cycle's automatic fetch. Replaces the bespoke `useState + setInterval`
 *   polling pattern. No effect while disabled (the caller drives those
 *   manually). The value is captured per deps-cycle: changing it requires a
 *   deps (or `enabled`) change to re-establish the interval. Non-positive /
 *   non-finite values disable polling.
 *
 *   🔴 A poll tick is a BACKGROUND read (UNREACHABLE-MODULE-BURNDOWN-001 unit D2, owner ruling
 *   D-1, D2-Q1 — background polling is not user activity): the fetcher receives
 *   `{ signal, background: true }` on a tick and `background: false` on every other launch, and
 *   MUST forward it to its client call (`api.get(url, { signal, background })`). `createApiClient`
 *   then never lets the tick keep a session alive; when it declines one
 *   (`BackgroundRequestPausedError`), the tick is SKIPPED — the resource returns to exactly the
 *   state it had before the tick, with no error. Guarded fleet-wide for the platform consoles by
 *   `tests/cross-app/contracts/platformConsolePollersBackground.contract.test.js`.
 * @property {boolean} [pausePollWhenHidden=false] - Skip a poll tick while
 *   `document.hidden` (the tab is in the background). The first tick after the tab
 *   is shown again reads as usual.
 */

/* global process */ // build-time-replaced by Vite; see the NODE_ENV note in the mount effect.

import { useReducer, useEffect, useMemo, useCallback, useRef } from 'react';
import { isBackgroundRequestPaused } from '@puffin-core/api-client/background-request';
import { ReadOnlySet } from '@puffin-core/read-only';

const STATUS = Object.freeze({
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  ERROR: 'error',
});

const ACTION = Object.freeze({
  BEGIN_LOADING: 'BEGIN_LOADING',
  BEGIN_REVALIDATING: 'BEGIN_REVALIDATING',
  RESOLVED: 'RESOLVED',
  REJECTED: 'REJECTED',
  REVALIDATE_REJECTED: 'REVALIDATE_REJECTED',
  SETTLE_DISABLED: 'SETTLE_DISABLED',
  // A background poll tick the session layer paused: the resource returns to its pre-tick state.
  TICK_SKIPPED: 'TICK_SKIPPED',
});

/** The keys {@link UseResourceOptions} declares — anything else throws. */
const OPTION_KEYS = new ReadOnlySet(['enabled', 'pollIntervalMs', 'pausePollWhenHidden']);

/** The two ways a fetch can be launched — they differ ONLY in what happens to the current data. */
const MODE = Object.freeze({ LOAD: 'load', REVALIDATE: 'revalidate' });

const BEGIN_ACTION_BY_MODE = Object.freeze({
  [MODE.LOAD]: ACTION.BEGIN_LOADING,
  [MODE.REVALIDATE]: ACTION.BEGIN_REVALIDATING,
});

const REJECT_ACTION_BY_MODE = Object.freeze({
  [MODE.LOAD]: ACTION.REJECTED,
  [MODE.REVALIDATE]: ACTION.REVALIDATE_REJECTED,
});

/**
 * Did `a` and `b` name the same key? Element-wise `Object.is`, exactly as React compares a
 * dependency array — so a caller's omitted `deps` (a fresh `[]` every render) is one stable key.
 * The state's `key` is `undefined` until its first launch.
 */
export function sameKey(a, b) {
  if (!Array.isArray(a) || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (!Object.is(a[i], b[i])) return false;
  }
  return true;
}

// A FRESH object per call, as BEGIN_LOADING has always returned: a shared
// frozen singleton would make a loading → loading dispatch bail out of the
// re-render it has always produced, and `refetch` is pinned byte-stable.
function loadingState(key) {
  return {
    status: STATUS.LOADING,
    data: undefined,
    error: undefined,
    revalidating: false,
    revalidateError: undefined,
    key,
  };
}

/**
 * What an ENABLED resource reports while its state belongs to no load of the CURRENT key — before
 * the first launch, or in the render whose deps just moved (the effect that begins the new key's
 * load runs after that commit). Frozen: it is a view, never reducer state.
 */
const PENDING_VIEW = Object.freeze({
  status: STATUS.LOADING,
  data: undefined,
  error: undefined,
  revalidating: false,
  revalidateError: undefined,
});

function idleState() {
  return {
    status: STATUS.IDLE,
    data: undefined,
    error: undefined,
    revalidating: false,
    revalidateError: undefined,
  };
}

function rejectedState(error, key) {
  return {
    status: STATUS.ERROR,
    data: undefined,
    error,
    revalidating: false,
    revalidateError: undefined,
    key,
  };
}

// Exported for unit-test access. The default branch is a safety net for
// any future action type added without a matching case — under-coverage of
// the branch is filed as a regression in the test (not papered over with
// an istanbul-ignore directive).
//
// The two REVALIDATE actions decide from the STATE, not from the caller: only a
// `ready` resource has data worth keeping, so from any other status a
// revalidate IS a load and its failure IS an error. A superseded fetch never
// reaches the reducer (its controller is aborted), so a REVALIDATE_REJECTED
// that arrives at a `ready` state always belongs to that state's own re-read.
//
// SETTLE_DISABLED runs on every DISABLED effect cycle, after that cycle's
// cleanup aborted whatever was in flight: an aborted load never settles on its
// own (its settlement is dropped), so a `loading` state returns to `idle` and a
// background re-read's `revalidating` flag drops. Anything else is already
// settled and is returned by identity (no re-render).
//
// Every launch carries the KEY (the caller's deps) it reads for, and the state
// records it (L-MIRROR2): the hook compares it with the render's deps, so the
// render that moves the deps never commits the previous key's answer. A
// BEGIN_REVALIDATING keeps the data only when it is the SAME key's ready data —
// a revalidate issued after the key moved is a load for the new key.
export function resourceReducer(state, action) {
  switch (action.type) {
    case ACTION.BEGIN_LOADING:
      return loadingState(action.key);
    case ACTION.BEGIN_REVALIDATING:
      return state.status === STATUS.READY && sameKey(state.key, action.key)
        ? { ...state, revalidating: true, revalidateError: undefined }
        : loadingState(action.key);
    case ACTION.RESOLVED:
      return {
        status: STATUS.READY,
        data: action.data,
        error: undefined,
        revalidating: false,
        revalidateError: undefined,
        key: action.key,
      };
    case ACTION.REJECTED:
      return rejectedState(action.error, action.key);
    case ACTION.REVALIDATE_REJECTED:
      return state.status === STATUS.READY
        ? { ...state, revalidating: false, revalidateError: action.error }
        : rejectedState(action.error, action.key);
    case ACTION.SETTLE_DISABLED:
      if (state.status === STATUS.LOADING) return idleState();
      return state.revalidating ? { ...state, revalidating: false } : state;
    case ACTION.TICK_SKIPPED:
      return action.previous;
    default:
      return state;
  }
}

/**
 * Wrap a non-Error rejection in an Error so the consumer always sees an
 * `Error` instance. String rejections preserve the message. Object
 * rejections with a `.message` property promote that to the Error
 * message. Other objects (e.g. `{}` from a thrown literal) produce an
 * Error with empty message so consumer fallbacks (`err.message ||
 * t('…')`) continue to work. The `String(value)` fallback is reserved for
 * primitives and exotic toString implementations.
 *
 * The failure itself is never lost: `status: 'error'` reports it whatever this
 * returns. When an exotic `toString` THROWS while the rejection is being read,
 * both the rejection and that throw are kept on an `AggregateError` (empty
 * message, so the consumer's translated fallback renders) rather than replaced
 * by an invented English sentence the old catch returned (`'Unknown error'`).
 */
function toError(value) {
  if (value instanceof Error) return value;
  if (typeof value === 'string') return new Error(value);
  if (value && typeof value === 'object') {
    if (typeof value.message === 'string') return new Error(value.message);
    // For objects without a `.message`, try `String(value)` — most objects
    // stringify to `'[object Object]'` which we collapse to empty so
    // consumer fallbacks (`err.message || t('…')`) work.
    try {
      const str = String(value);
      if (str === '[object Object]') return new Error('');
      return new Error(str);
    } catch (stringifyError) {
      return new AggregateError([value, stringifyError], '');
    }
  }
  // Primitive fallback. JS guarantees `String(<primitive>)` is total for
  // number / bigint / boolean / undefined / null / symbol — no try/catch
  // needed here. The object branch above handles exotic toString throws.
  return new Error(String(value));
}

/**
 * @private Dev-only churn tracker for {@link useResource}. `state` is the
 * hook instance's mutable churn record; `deps` the caller's current array.
 * Pure bookkeeping + a single console.error — never throws, never changes
 * hook behavior.
 */
export function trackDepChurn(state, deps, now = Date.now()) {
  const prev = state.prevDeps;
  state.prevDeps = deps;
  if (!prev || state.warned || !Array.isArray(deps) || prev.length !== deps.length) return;

  let identityChanged = false;
  let comparable = true;
  let allValueEqual = true;
  for (let i = 0; i < deps.length; i++) {
    if (Object.is(prev[i], deps[i])) continue;
    identityChanged = true;
    const a = prev[i];
    const b = deps[i];
    // Two DIFFERENT function identities for the same dep slot every cycle is
    // the `t`-from-a-fresh-mock shape — treat as churn-suspect (value-equal).
    if (typeof a === 'function' && typeof b === 'function') continue;
    try {
      if (JSON.stringify(a) !== JSON.stringify(b)) {
        allValueEqual = false;
        break;
      }
    } catch {
      comparable = false;
      break;
    }
  }
  if (!identityChanged || !comparable || !allValueEqual) {
    state.count = 0;
    return;
  }

  if (now - state.windowStart > 10000) {
    state.windowStart = now;
    state.count = 0;
  }
  state.count += 1;
  if (state.count >= 10) {
    state.warned = true;
    // Intentional dev-time diagnostic; muted in production builds via the
    // NODE_ENV gate at the call site.
    console.error(
      '[useResource] dep-churn detected: the dependency array changed identity ' +
        `${state.count} times in ${Math.round((now - state.windowStart) / 1000)}s ` +
        'with deep-equal values — this loops the fetch (API hammering in ' +
        'production; starved timers/hangs in tests). Hoist inline object/array ' +
        'literals to stable references (module const / useMemo) and keep ' +
        'identity-unstable functions (e.g. `t`) OUT of the deps — read them ' +
        'through a ref instead.',
    );
  }
}

/**
 * Validate and normalise the options record (see "`enabled`" in the file
 * docblock). Only a positive finite `pollIntervalMs` enables polling.
 *
 * @param {UseResourceOptions|undefined} options
 * @returns {{ enabled: boolean, pollIntervalMs: number, pausePollWhenHidden: boolean }}
 */
function readOptions(options) {
  if (!options) return { enabled: true, pollIntervalMs: 0, pausePollWhenHidden: false };
  for (const key of Object.keys(options)) {
    if (!OPTION_KEYS.has(key)) {
      throw new TypeError(
        `useResource: unknown option "${key}" — the options are ${[...OPTION_KEYS].join(', ')} ` +
          '(`initial` was replaced by `enabled`, which is read on every deps cycle).',
      );
    }
  }
  const { enabled, pollIntervalMs, pausePollWhenHidden } = options;
  return {
    enabled: enabled === undefined || Boolean(enabled),
    pausePollWhenHidden: Boolean(pausePollWhenHidden),
    pollIntervalMs:
      typeof pollIntervalMs === 'number' && Number.isFinite(pollIntervalMs) && pollIntervalMs > 0
        ? pollIntervalMs
        : 0,
  };
}

/**
 * Async-data resource primitive.
 *
 * @template T
 * @param {(ctx: {signal: AbortSignal, background: boolean}) => Promise<T>} fetcher
 *   Async function that returns the data. Receives `{signal, background}` so it can
 *   thread the abort — and, on a poll tick, the background flag — into its
 *   underlying HTTP call.
 * @param {ReadonlyArray<*>} [deps=[]] - Dependency array; the fetcher
 *   re-runs whenever any dep changes (same semantics as `useEffect`).
 * @param {UseResourceOptions} [options]
 * @returns {ResourceState}
 */
export function useResource(fetcher, deps = [], options) {
  const { enabled, pollIntervalMs, pausePollWhenHidden } = readOptions(options);

  // The reducer starts `idle`; an ENABLED resource reports `loading` from its
  // very first render through the derivation in the returned memo, so there is
  // one rule for a mount and for a later `enabled` flip alike.
  const [state, dispatch] = useReducer(resourceReducer, undefined, idleState);

  // The latest committed state, read by a poll tick so a paused tick can return to it.
  const stateRef = useRef(state);
  stateRef.current = state;

  // Mutable ref to the latest fetcher — keeps `runFetch` stable across
  // renders while still picking up fresh closures. Avoids stale captures.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // The KEY every launch reads for — the caller's `deps` of the latest render
  // (a ref, like `fetcher`, so `launch` stays stable). The reducer records it
  // on every state it produces; see "no committed render pairs a key with
  // another key's answer" below.
  const keyRef = useRef(deps);
  keyRef.current = deps;

  // Mutable ref to the currently in-flight AbortController. Each fetch
  // replaces the previous controller and aborts it. Unmount aborts
  // whatever is current.
  const controllerRef = useRef(null);

  // Mutable ref to the normalized poll settings. Captured via a ref (like
  // `fetcher`) so the auto-fire effect's dependency list stays the caller's
  // `deps` array plus `enabled`.
  const pollRef = useRef({ intervalMs: 0, pauseWhenHidden: false });
  pollRef.current = { intervalMs: pollIntervalMs, pauseWhenHidden: pausePollWhenHidden };

  // ── Dev-only dep-churn guard (PLN-BUILDER-CANON-001 G5) ────────────────
  // The refetch-loop footgun: a dep whose IDENTITY changes every render while
  // its VALUE does not (an inline `{...plan}` literal, a per-render `t` from
  // a mocked `useTranslation`) re-fires this hook's effect on every state
  // flush — an unbounded fetch loop that hammers the API in production and
  // starves `waitFor` timers into multi-minute hangs in tests (observed
  // twice, 2026-07-24). React cannot see it (the deps DID change by
  // `Object.is`); this guard can: when the effect re-fires but every changed
  // dep is value-equal (or an unverifiable function), count it as churn and
  // console.error once past a threshold. Diagnostic only — behavior is
  // never altered, and the whole block compiles out of production bundles
  // via the NODE_ENV gate.
  const churnRef = useRef({ prevDeps: null, count: 0, windowStart: 0, warned: false });

  // ONE launcher for both modes: abort, controller, sync-throw capture and the
  // stale-settlement guard are identical; the mode only picks which BEGIN and
  // which REJECT action the reducer sees.
  const launch = useCallback((mode, background = false) => {
    // Abort any in-flight fetch so the resolution / rejection of the
    // previous attempt cannot stomp the new state.
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    const controller = new AbortController();
    controllerRef.current = controller;
    const key = keyRef.current;
    const previous = stateRef.current;
    dispatch({ type: BEGIN_ACTION_BY_MODE[mode], key });

    let promise;
    try {
      const result = fetcherRef.current({ signal: controller.signal, background });
      promise = Promise.resolve(result);
    } catch (syncErr) {
      promise = Promise.reject(syncErr);
    }

    return promise.then(
      (data) => {
        if (controller.signal.aborted) return;
        dispatch({ type: ACTION.RESOLVED, data, key });
      },
      (err) => {
        if (controller.signal.aborted) return;
        // D2-Q1: the session layer declined a background tick (nobody at the page) — skip it.
        if (background && isBackgroundRequestPaused(err)) {
          dispatch({ type: ACTION.TICK_SKIPPED, previous });
          return;
        }
        dispatch({ type: REJECT_ACTION_BY_MODE[mode], error: toError(err), key });
      },
    );
  }, []);

  const runFetch = useCallback(() => launch(MODE.LOAD), [launch]);
  const revalidate = useCallback(() => launch(MODE.REVALIDATE), [launch]);

  // Auto-fire on mount + every deps / `enabled` change while ENABLED; while
  // disabled, settle whatever the previous cycle's cleanup aborted.
  // When `pollIntervalMs` is set (and enabled), a `setInterval` re-runs the
  // fetcher on that cadence. Cleanup always aborts the current controller AND
  // clears the interval so an unmount mid-fetch never produces a post-unmount
  // setState and no stray timer survives the unmount/deps-change.
  useEffect(() => {
    // NAV-ICON-COVERAGE-001 (2026-09-03) — this gate read
    // `const runtimeProcess = globalThis.process;` and was therefore ALWAYS
    // false in a browser (no browser has a global `process`, and the shared
    // Vite factory declares no `define` block), so the dep-churn detector
    // NEVER RAN outside jest. That is the worst possible file for this bug:
    // the failure it detects is, in its own message, "API hammering in
    // production" — a browser-runtime fault it was structurally unable to
    // see. Vite replaces the BARE literal; the global DECLARATION at the top
    // of this file satisfies no-undef by declaring the token rather than
    // suppressing the rule. See `strings/index.js` ruling 101.
    if (process.env.NODE_ENV !== 'production') {
      trackDepChurn(churnRef.current, deps);
    }
    let intervalId = null;
    if (enabled) {
      runFetch();
      const ms = pollRef.current.intervalMs;
      if (ms) {
        intervalId = setInterval(() => {
          // page-shell is web-only: a DOM `document` is always present here.
          if (pollRef.current.pauseWhenHidden && document.hidden) {
            return;
          }
          launch(MODE.LOAD, true);
        }, ms);
      }
    } else {
      dispatch({ type: ACTION.SETTLE_DISABLED });
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `deps` is the caller-supplied reactivity surface (the RES-PRIM-001 primitive contract), spread so `enabled` joins it: the effect re-fires only when the caller's `deps` or the auto-load switch change, mirroring React Query / SWR key + `enabled` semantics. `runFetch` and `launch` are stable (`launch` is memoized with an empty dep array, `runFetch` over it) and read `fetcherRef`/`pollRef` via refs, so they neither need nor belong in this array.
  }, [...deps, enabled]);

  // No committed render pairs an ENABLED resource with an answer that is not
  // its current key's (L-MIRROR2). The effect that begins a load runs AFTER the
  // commit, so two renders would otherwise commit a stale view: the first render
  // (or the one that turned `enabled` on) would report `idle`, and the render
  // whose deps just moved would report the PREVIOUS key's `ready` data — a
  // product switch painted the previous product's rows for a frame, and every
  // `useResourceDraft` seeded from the wrong source. Both report `loading`
  // instead, with no data and no error. The manual mode keeps its last answer
  // across a deps change: that is its contract (a lookup's results stand until
  // the reader runs the next one).
  // (An idle state carries no key, so it is never the current key's answer.)
  const view = enabled && !sameKey(state.key, deps) ? PENDING_VIEW : state;

  // Identity stability: useReducer guarantees `state` is the SAME object
  // reference across renders unless dispatch fires. `runFetch` is stable via
  // `useCallback`. Therefore the returned object only needs to change identity
  // when the underlying state changes — `useMemo` enforces that. Without
  // this, every render of a parent component would produce a fresh result
  // object, cascading reference-invalidations through downstream `useMemo`
  // consumers (the screen-204 closure regression the OOM debug pass surfaced
  // on 2026-05-11).
  return useMemo(
    () => ({
      status: view.status,
      data: view.data,
      error: view.error,
      refetch: runFetch,
      revalidate,
      revalidating: view.revalidating,
      revalidateError: view.revalidateError,
    }),
    [view, runFetch, revalidate],
  );
}

export default useResource;
